import React, { useState, useEffect, useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { Text, StatusType, useAdpAnalytics, openDrawer } from '@innovyze/stylovyze';
import { useDispatch } from 'react-redux';
import { useGlobalization } from '@Translations';

import { selectDeteriorationModel } from '@Selectors/deteriorationModels.selector';
import HelpDrawer, { HELP_DRAWER } from '@Components/HelpPageDrawer';
import { ModelResultsSelectionCard } from './ModelResultsSelectionCard';
import { AssetsResults } from './AssetsResults';
import { GroupsResults } from './GroupsResults';

interface DeteriorationModelResultsTabProps {
	onNavigateToGradeSetup?: () => void;
}

export const DeteriorationModelResultsTab = ({ onNavigateToGradeSetup }: DeteriorationModelResultsTabProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const [selectedTab, setSelectedTab] = useState<'assets' | 'groups'>('assets');
	const [groupFilter, setGroupFilter] = useState<string | null>(null);

	const handleShowAssetsForGroup = (groupName: string) => {
		setGroupFilter(groupName);
		setSelectedTab('assets');
	};

	const deteriorationModel = selectDeteriorationModel();
	const currentDeteriorationModel = deteriorationModel.deteriorationModel;

	// Check if model is currently running
	const isModelRunning = currentDeteriorationModel?.status === StatusType.inProgress;

	const adpClient = useAdpAnalytics();
	const lastTrackedResultsId = useRef<string | null>(null);

	useEffect(() => {
		const resultsId = currentDeteriorationModel?.latestRunResultsId;
		const hasPopulatedResults = !!resultsId && !!currentDeteriorationModel?.runAt && !isModelRunning;

		if (!hasPopulatedResults) return;
		if (lastTrackedResultsId.current === resultsId) return;

		adpClient?.track('USER_VIEW', {
			id: 'deterioration-model-results',
			name: 'Deterioration Model Results',
			type: 'tab',
			attributes: {
				modelid: currentDeteriorationModel?.id ?? '',
				modelname: currentDeteriorationModel?.name ?? '',
				runresultsid: resultsId ?? '',
				assetgroupsetid: currentDeteriorationModel?.assetGroupSetId ?? '',
			},
		});
		lastTrackedResultsId.current = resultsId;
	}, [
		adpClient,
		currentDeteriorationModel?.id,
		currentDeteriorationModel?.latestRunResultsId,
		currentDeteriorationModel?.runAt,
		isModelRunning,
	]);

	const renderBody = (): JSX.Element => {
		if (isModelRunning) {
			return (
				<ResultsInProgressEmptyState
					message={t('Calculation in progress, please check back later.')}
					isInProgress={true}
				/>
			);
		}

		if (currentDeteriorationModel?.latestRunResultsId && currentDeteriorationModel?.runAt) {
			return (
				<>
					<ModelResultsSelectionCard
						selectedTab={selectedTab}
						setSelectedTab={setSelectedTab}
						onNavigateToGradeSetup={onNavigateToGradeSetup}
						onLearnMore={() => dispatch(openDrawer(HELP_DRAWER.MODEL_RESULTS))}
					/>
					{selectedTab === 'assets' ? (
						<AssetsResults initialGroupFilter={groupFilter} />
					) : (
						<GroupsResults onShowAssetsForGroup={handleShowAssetsForGroup} />
					)}
				</>
			);
		}

		return (
			<ResultsInProgressEmptyState
				message={t('Run the deterioration model to view results. Click the Run button in the Configuration tab.')}
				isInProgress={false}
			/>
		);
	};

	return (
		<Stack direction="column" spacing={2}>
			<HelpDrawer drawerID={HELP_DRAWER.MODEL_RESULTS} />
			{renderBody()}
		</Stack>
	);
};

export const ResultsInProgressEmptyState: React.FC<{ message: string; isInProgress?: boolean }> = ({
	message,
	isInProgress = false,
}) => {
	const { t } = useGlobalization();

	return (
		<Box
			display="flex"
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			minHeight="400px"
			sx={{ textAlign: 'center' }}>
			{/* Box icon similar to screenshot */}
			<svg width="120" height="120" viewBox="0 0 120 120" style={{ marginBottom: '24px', opacity: 0.6 }}>
				{/* Main box body */}
				<rect x="15" y="30" width="90" height="70" fill="#f5f5f5" stroke="#ccc" strokeWidth="2" rx="4" />
				{/* Box lid */}
				<rect x="25" y="20" width="70" height="20" fill="#f5f5f5" stroke="#ccc" strokeWidth="2" rx="3" />
				{/* Box handle */}
				<rect x="50" y="15" width="20" height="8" fill="none" stroke="#ccc" strokeWidth="2" rx="4" />
				{/* Package symbol */}
				<line x1="30" y1="50" x2="90" y2="50" stroke="#ddd" strokeWidth="1" />
				<line x1="60" y1="35" x2="60" y2="85" stroke="#ddd" strokeWidth="1" />
			</svg>
			<Text variant="heading-medium-strong" sx={{ mb: 1 }}>
				{isInProgress ? t('Results calculation in progress') : t('No results available')}
			</Text>
			<Text variant="body1" sx={{ color: 'text.secondary' }}>
				{message}
			</Text>
		</Box>
	);
};
